import { useEffect } from 'react';

import { DataPoint, Margin } from './types';

interface UseChartEffectsProps {
  spotPrice: number;
  strikePrice?: number;
  priceHistory: DataPoint[];
  isInitialized: boolean;
  setIsInitialized: (value: boolean) => void;
  smoothedCenterPrice: number;
  smoothedYBounds: [number, number];
  yScale: [number, number];
  lastAutoScaleStrike: number | null;
  savedView: {
    zoom: number;
    bounds: [number, number];
    centerPrice: number;
  } | null;
  isDraggingStrike: React.MutableRefObject<boolean>;
  strikePrices: number[];
  onStrikePriceSelect?: (price: number) => void;
  previewStrikePrice: number | null;
  nearestValidStrike: number | null;
  setSmoothedCenterPrice: (value: number | ((prev: number) => number)) => void;
  setSmoothedSpotPrice: (value: number | ((prev: number) => number)) => void;
  setSmoothedYBounds: (
    value: [number, number] | ((prev: [number, number]) => [number, number]),
  ) => void;
  setYScale: (
    value: [number, number] | ((prev: [number, number]) => [number, number]),
  ) => void;
  setLastAutoScaleStrike: (value: number | null) => void;
  setSavedView: (
    value: {
      zoom: number;
      bounds: [number, number];
      centerPrice: number;
    } | null,
  ) => void;
  updatePreviewStrike: (rawPrice: number, y: number, nearest: number) => void;
  clearPreviewStrike: () => void;
  containerRef: React.RefObject<HTMLDivElement | null>;
  yScaleRef: React.RefObject<any>;
  marginRef: React.RefObject<Margin>;
}

export function useChartEffects({
  spotPrice,
  strikePrice,
  priceHistory,
  isInitialized,
  setIsInitialized,
  smoothedCenterPrice,
  smoothedYBounds,
  yScale,
  lastAutoScaleStrike,
  savedView,
  isDraggingStrike,
  strikePrices,
  onStrikePriceSelect,
  previewStrikePrice,
  nearestValidStrike,
  setSmoothedCenterPrice,
  setSmoothedSpotPrice,
  setSmoothedYBounds: _setSmoothedYBounds,
  setYScale: _setYScale,
  setLastAutoScaleStrike,
  setSavedView,
  updatePreviewStrike,
  clearPreviewStrike,
  containerRef,
  yScaleRef,
  marginRef,
}: UseChartEffectsProps) {
  // Container resize effect
  useEffect(() => {
    const updateDimensions = () => {
      if (containerRef.current) {
        const { width: _width, height: _height } =
          containerRef.current.getBoundingClientRect();
        // This would need to be handled differently - we'd need to pass setDimensions as a prop
        // For now, we'll handle this in the main component
      }
    };

    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    const resizeObserver = new ResizeObserver(updateDimensions);
    if (containerRef.current) {
      resizeObserver.observe(containerRef.current);
    }

    return () => {
      window.removeEventListener('resize', updateDimensions);
      resizeObserver.disconnect();
    };
  }, []);

  // Disable pull-to-refresh only within chart area
  useEffect(() => {
    const preventPullToRefresh = (e: TouchEvent) => {
      if (isDraggingStrike.current) {
        return;
      }

      // Only prevent pull-to-refresh if touch is within chart container
      if (containerRef.current && e.target) {
        const isWithinChart = containerRef.current.contains(e.target as Node);
        if (!isWithinChart) {
          return; // Allow normal behavior outside chart
        }

        // Don't prevent default if touching the recenter button or manage-orders button
        const target = e.target as HTMLElement;
        const isRecenterButton = target.closest('#chart-recenter-button');
        const isManageOrdersButton = target.closest(
          '#chart-manage-orders-button',
        );
        if (isRecenterButton || isManageOrdersButton) {
          return; // Allow button clicks
        }

        if (e.touches.length === 1) {
          const scrollTop =
            document.documentElement.scrollTop || document.body.scrollTop;

          if (scrollTop === 0) {
            e.preventDefault();
          }
        }

        if (e.touches.length > 1) {
          e.preventDefault();
        }
      }
    };

    const preventScrollBehavior = (e: TouchEvent) => {
      if (isDraggingStrike.current) {
        return;
      }

      // Only prevent scroll if touch is within chart container
      if (containerRef.current && e.target) {
        const isWithinChart = containerRef.current.contains(e.target as Node);
        if (!isWithinChart) {
          return; // Allow normal behavior outside chart
        }

        // Don't prevent default if touching the recenter button or manage-orders button
        const target = e.target as HTMLElement;
        const isRecenterButton = target.closest('#chart-recenter-button');
        const isManageOrdersButton = target.closest(
          '#chart-manage-orders-button',
        );
        if (isRecenterButton || isManageOrdersButton) {
          return; // Allow button clicks
        }

        if (e.touches.length > 1) {
          e.preventDefault();
          e.stopPropagation();
        }
      }
    };

    document.body.style.overscrollBehavior = 'none';
    (document.body.style as any).webkitOverscrollBehavior = 'none';
    document.documentElement.style.overscrollBehavior = 'none';
    (document.documentElement.style as any).webkitOverscrollBehavior = 'none';

    document.addEventListener('touchstart', preventPullToRefresh, {
      passive: false,
    });
    document.addEventListener('touchmove', preventScrollBehavior, {
      passive: false,
    });
    return () => {
      document.body.style.overscrollBehavior = '';
      (document.body.style as any).webkitOverscrollBehavior = '';
      document.documentElement.style.overscrollBehavior = '';
      (document.documentElement.style as any).webkitOverscrollBehavior = '';
      document.removeEventListener('touchstart', preventPullToRefresh);
      document.removeEventListener('touchmove', preventScrollBehavior);
    };
  }, []);

  // Initialize and smoothly transition the center price for Y axis
  useEffect(() => {
    if (spotPrice <= 0) {
      return;
    }

    let targetCenterPrice = spotPrice;
    if (strikePrice && strikePrice > 0) {
      targetCenterPrice = strikePrice;
    }

    if (!isInitialized) {
      setSmoothedCenterPrice(targetCenterPrice);
      setSmoothedSpotPrice(spotPrice);
      setIsInitialized(true);
      return;
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const speed = strikePrice ? 0.12 : 0.02;

    const interval = setInterval(() => {
      setSmoothedCenterPrice(current => {
        const newPrice = lerp(current, targetCenterPrice, speed);

        if (Math.abs(newPrice - targetCenterPrice) < 0.01) {
          clearInterval(interval);
          return targetCenterPrice;
        }

        return newPrice;
      });
    }, 16);

    return () => clearInterval(interval);
  }, [spotPrice, strikePrice, isInitialized]);

  // Smooth the spot price itself for the line endpoint
  useEffect(() => {
    if (spotPrice <= 0 || !isInitialized) {
      return;
    }

    const lerp = (a: number, b: number, t: number) => a + (b - a) * t;
    const speed = 0.08;

    const interval = setInterval(() => {
      setSmoothedSpotPrice(current => {
        const newPrice = lerp(current, spotPrice, speed);

        if (Math.abs(newPrice - spotPrice) < 0.01) {
          clearInterval(interval);
          return spotPrice;
        }

        return newPrice;
      });
    }, 16);

    return () => clearInterval(interval);
  }, [spotPrice, isInitialized]);

  // Track strike selection for saved view management (no auto-scaling)
  useEffect(() => {
    if (spotPrice <= 0 || !isInitialized) {
      return;
    }

    if (strikePrice === lastAutoScaleStrike) {
      return;
    }

    // When deselecting strike, clear saved view
    if (!strikePrice && lastAutoScaleStrike !== null) {
      setLastAutoScaleStrike(null);
      setSavedView(null);
      return;
    }

    // When selecting a new strike, save current view but don't auto-scale
    if (strikePrice && strikePrice > 0 && !savedView) {
      const basePrices = [...priceHistory.map(d => d.price), spotPrice];
      const basePriceBounds: [number, number] = [
        Math.min(...basePrices),
        Math.max(...basePrices),
      ];

      // Calculate current zoom level from yScale range vs base range
      const currentRange = yScale[1] - yScale[0];
      const baseRange = basePriceBounds[1] - basePriceBounds[0];
      const currentZoom = baseRange / currentRange;

      setSavedView({
        zoom: currentZoom,
        bounds: smoothedYBounds,
        centerPrice: smoothedCenterPrice,
      });
    }

    setLastAutoScaleStrike(strikePrice ?? null);
  }, [
    spotPrice,
    strikePrice,
    priceHistory,
    isInitialized,
    lastAutoScaleStrike,
    smoothedYBounds,
    smoothedCenterPrice,
    savedView,
    yScale,
    setLastAutoScaleStrike,
    setSavedView,
  ]);

  // Global handlers for strike price dragging
  useEffect(() => {
    const handleGlobalMouseMove = (e: MouseEvent) => {
      if (
        isDraggingStrike.current &&
        containerRef.current &&
        strikePrices.length > 0 &&
        yScaleRef.current
      ) {
        const rect = containerRef.current.getBoundingClientRect();
        const y = e.clientY - rect.top - marginRef.current.top;
        const rawPrice = yScaleRef.current.invert(y);

        const nearest = strikePrices.reduce((prev, curr) =>
          Math.abs(curr - rawPrice) < Math.abs(prev - rawPrice) ? curr : prev,
        );

        updatePreviewStrike(rawPrice, y, nearest);
      }
    };

    const handleGlobalTouchMove = (e: TouchEvent) => {
      if (
        isDraggingStrike.current &&
        e.touches.length === 1 &&
        containerRef.current &&
        strikePrices.length > 0 &&
        yScaleRef.current
      ) {
        const rect = containerRef.current.getBoundingClientRect();
        const y = e.touches[0].clientY - rect.top - marginRef.current.top;
        const rawPrice = yScaleRef.current.invert(y);

        const nearest = strikePrices.reduce((prev, curr) =>
          Math.abs(curr - rawPrice) < Math.abs(prev - rawPrice) ? curr : prev,
        );

        updatePreviewStrike(rawPrice, y, nearest);
      }
    };

    const handleGlobalMouseUp = () => {
      if (isDraggingStrike.current) {
        isDraggingStrike.current = false;

        if (nearestValidStrike !== null && onStrikePriceSelect) {
          onStrikePriceSelect(nearestValidStrike);
        }
        clearPreviewStrike();
      }
    };

    const handleGlobalTouchEnd = () => {
      if (isDraggingStrike.current) {
        isDraggingStrike.current = false;

        if (nearestValidStrike !== null && onStrikePriceSelect) {
          onStrikePriceSelect(nearestValidStrike);
        }
        clearPreviewStrike();
      }
    };

    document.addEventListener('mousemove', handleGlobalMouseMove);
    document.addEventListener('touchmove', handleGlobalTouchMove);
    document.addEventListener('mouseup', handleGlobalMouseUp);
    document.addEventListener('touchend', handleGlobalTouchEnd);

    return () => {
      document.removeEventListener('mousemove', handleGlobalMouseMove);
      document.removeEventListener('touchmove', handleGlobalTouchMove);
      document.removeEventListener('mouseup', handleGlobalMouseUp);
      document.removeEventListener('touchend', handleGlobalTouchEnd);
    };
  }, [
    strikePrices,
    onStrikePriceSelect,
    previewStrikePrice,
    isDraggingStrike,
    containerRef,
    yScaleRef,
    marginRef,
    updatePreviewStrike,
    nearestValidStrike,
    clearPreviewStrike,
  ]);
}
